import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useLocale } from '../contexts/LocaleContext';
import { useIsMobile } from '../hooks/use-mobile';
import { getEditorKeyboardShortcuts, formatShortcutKey } from '../utils/editor-shortcuts';
import { UserFeedbackForm } from './UserFeedbackForm';
import { FaqList } from './FaqList';
import { Separator } from './ui/separator';
import { Kbd } from './ui/kbd';
import { cn } from '../lib/utils';

type EditorHelpDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const EditorHelpDialog: React.FC<EditorHelpDialogProps> = ({ open, onOpenChange }) => {
  const { t } = useLocale();
  const isMobile = useIsMobile();
  const shortcuts = getEditorKeyboardShortcuts();
  const half = Math.ceil(shortcuts.length / 2);
  const leftColumn = shortcuts.slice(0, half);
  const rightColumn = shortcuts.slice(half);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[90vw] max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
          <DialogTitle tabIndex={-1} data-dialog-autofocus>
            {t('help')}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto px-6 pb-6">
          {!isMobile && (
            <>
              <h3 className="text-sm font-semibold mt-2 mb-3">{t('editor_shortcuts_title')}</h3>
              <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm">
                  {leftColumn.map(({ key, descriptionKey }) => (
                    <React.Fragment key={`${key}-${descriptionKey}`}>
                      <dt className="text-muted-foreground whitespace-nowrap">
                        <Kbd>{formatShortcutKey(key)}</Kbd>
                      </dt>
                      <dd className="text-foreground">{t(descriptionKey)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm">
                  {rightColumn.map(({ key, descriptionKey }) => (
                    <React.Fragment key={`${key}-${descriptionKey}`}>
                      <dt className="text-muted-foreground whitespace-nowrap">
                        <Kbd>{formatShortcutKey(key)}</Kbd>
                      </dt>
                      <dd className="text-foreground">{t(descriptionKey)}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
              <Separator className="my-6" />
            </>
          )}
          <h3 className={cn('text-sm font-semibold mb-3', isMobile && 'mt-2')}>{t('landing_faq_title')}</h3>
          <FaqList />
          <Separator className="my-6" />
          <h3 className="text-sm font-semibold mb-3">{t('ask_question_or_request')}</h3>
          <div data-dialog-skip-autofocus>
            <UserFeedbackForm
              onClose={() => onOpenChange(false)}
              onSubmitted={() => onOpenChange(false)}
              showCancelButton={false}
              autoFocus={false}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
